import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { withFragments } from '@sorare/core/src/lib/gql';
import type { Color } from '@sorare/core/src/style/types';

import type { SteppedPrimaryOffer_primaryOffer } from './__generated__/steppedPrimaryOffer.graphql.ts';

type SteppedPrimaryOfferInput = {
  remainingAtCurrentPrice?: number | null;
  quantityPerStep?: number | null;
  soldOut?: boolean | null;
} | null;

type SteppedPrimaryOffer = {
  isStepped: boolean;
  soldOut: boolean;
  remaining: number | null;
  ratio: number | null;
  color: Color;
};

const SUPPLY_COLORS = {
  high: 'var(--c-score-mediumHigh)', // green
  medium: 'var(--c-score-mediumLow)', // gold
  low: 'var(--c-score-low)', // orange
  empty: 'var(--c-score-veryLow)', // red
} as const satisfies Record<string, Color>;

const getSupplyColor = (ratio: number): Color => {
  if (ratio >= 0.66) return SUPPLY_COLORS.high;
  if (ratio >= 0.4) return SUPPLY_COLORS.medium;
  if (ratio >= 0.2) return SUPPLY_COLORS.low;
  return SUPPLY_COLORS.empty;
};

export const getSteppedPrimaryOffer = withFragments(
  (offer: SteppedPrimaryOfferInput): SteppedPrimaryOffer => {
    const remaining = offer?.remainingAtCurrentPrice ?? null;
    const quantityPerStep = offer?.quantityPerStep;
    const soldOut = !!offer?.soldOut;
    const isStepped = remaining != null || offer?.soldOut != null;

    const rawRatio =
      quantityPerStep && quantityPerStep > 0 && remaining != null
        ? Math.min(1, Math.max(0, remaining / quantityPerStep))
        : null;
    const ratio = soldOut ? 0 : rawRatio;

    return {
      isStepped,
      soldOut,
      remaining,
      ratio,
      color: getSupplyColor(ratio ?? 1),
    };
  },
  {
    primaryOffer: gql`
      fragment SteppedPrimaryOffer_primaryOffer on TokenPrimaryOffer {
        id
        remainingAtCurrentPrice
        quantityPerStep
        soldOut
      }
    ` as TypedDocumentNode<SteppedPrimaryOffer_primaryOffer>,
  }
);
